import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { KeyRound, MonitorCheck, RefreshCw, ShieldCheck, ShieldOff } from 'lucide-react';
import { useCurrentUser } from '@/auth/AuthProvider';
import { Button, ErrorState, FormField, Loading, Modal, PageHeader, Panel, StatusBadge, TextField, useToast } from '@/components';
import { errorMessage } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { CERT_STATUS_LABELS, mfaApi, securityKeys, useClientInfo, useMfaStatus } from './api';
import { MfaEnrollment, RecoveryCodes } from './MfaEnrollment';
import './security.css';

/** パスワードの再入力ダイアログ（無効化・リカバリーコード再発行） */
function PasswordDialog({
  title,
  description,
  confirmLabel,
  tone,
  onSubmit,
  onClose,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  tone?: 'danger';
  onSubmit: (password: string) => Promise<void>;
  onClose: () => void;
}) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!password) {
      setError('パスワードを入力してください');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onSubmit(password);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <Button onClick={onClose}>キャンセル</Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} loading={busy} onClick={() => void submit()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} noValidate>
        <p className="bs-modal-text">{description}</p>
        <FormField label="現在のパスワード" error={error ?? undefined}>
          <TextField type="password" autoComplete="current-password" data-autofocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </FormField>
      </form>
    </Modal>
  );
}

/** 2段階認証の設定（本人）と接続環境の確認 */
export function AccountSecurityPage() {
  const me = useCurrentUser();
  const qc = useQueryClient();
  const toast = useToast();
  const status = useMfaStatus();
  const client = useClientInfo();
  const [mode, setMode] = useState<'idle' | 'enroll' | 'disable' | 'regenerate'>('idle');
  const [codes, setCodes] = useState<string[] | null>(null);

  const refresh = () => qc.invalidateQueries({ queryKey: securityKeys.mfa });

  if (status.isPending) return <Loading block />;
  if (status.error) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  const s = status.data;

  return (
    <>
      <PageHeader
        eyebrow="アカウント"
        title="2段階認証の設定"
        description="ログイン時に、パスワードに加えてスマートフォンの認証アプリに表示される確認コードを入力する設定です。"
        meta={s.enabled ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>未設定</StatusBadge>}
      />

      {codes ? (
        <Panel title="新しいリカバリーコード">
          <RecoveryCodes
            codes={codes}
            loginId={me.loginId}
            doneLabel="閉じる"
            onDone={() => {
              setCodes(null);
              void refresh();
            }}
          />
        </Panel>
      ) : mode === 'enroll' ? (
        <Panel title="認証アプリの登録">
          <MfaEnrollment
            onCancel={() => setMode('idle')}
            onEnabled={(r) => {
              setMode('idle');
              setCodes(r.recoveryCodes);
              toast.success('2段階認証を有効にしました', '次回のログインから確認コードの入力が必要です。');
            }}
          />
        </Panel>
      ) : (
        <Panel title="2段階認証（認証アプリ）">
          <dl className="bs-sec-kv">
            <dt>状態</dt>
            <dd>{s.enabled ? <StatusBadge tone="success" dot>有効</StatusBadge> : <StatusBadge dot>未設定</StatusBadge>}</dd>
            <dt>設定区分</dt>
            <dd>
              {s.required ? (
                <>
                  <StatusBadge tone="accent">必須</StatusBadge>
                  <span className="bs-sec-kv__note">管理者により必須に設定されています（無効にはできません）</span>
                </>
              ) : (
                <>
                  <StatusBadge>任意</StatusBadge>
                  <span className="bs-sec-kv__note">ご自身の判断で有効・無効を切り替えられます</span>
                </>
              )}
            </dd>
            {s.enabled && (
              <>
                <dt>有効にした日時</dt>
                <dd className="bs-num">{formatDateTime(s.enabledAt) || '—'}</dd>
                <dt>未使用のリカバリーコード</dt>
                <dd>
                  <span className="bs-num">{s.recoveryCodesRemaining}</span> 個
                  {s.recoveryCodesRemaining <= 3 && <span className="bs-sec-kv__warn">残りが少なくなっています。再発行してください。</span>}
                </dd>
              </>
            )}
          </dl>
          <div className="bs-mfa-actions bs-mfa-actions--start">
            {!s.enabled && (
              <Button variant="primary" icon={<ShieldCheck size={16} />} onClick={() => setMode('enroll')}>
                2段階認証を有効にする
              </Button>
            )}
            {s.enabled && (
              <Button icon={<RefreshCw size={16} />} onClick={() => setMode('regenerate')}>
                リカバリーコードを再発行
              </Button>
            )}
            {s.enabled && !s.required && (
              <Button variant="danger" icon={<ShieldOff size={16} />} onClick={() => setMode('disable')}>
                2段階認証を無効にする
              </Button>
            )}
          </div>
        </Panel>
      )}

      <Panel title="接続環境" description="IP アドレス制限・端末証明書の確認に使います。利用できない場合は、この内容を担当者へお伝えください。">
        {client.data ? (
          <dl className="bs-sec-kv">
            <dt>接続元 IP アドレス</dt>
            <dd className="bs-num">{client.data.ip ?? '不明'}</dd>
            <dt>端末証明書</dt>
            <dd>
              {client.data.certificate.presented ? (
                <>
                  <MonitorCheck size={15} aria-hidden className="bs-sec-kv__icon" />
                  {client.data.certificate.registered
                    ? `${client.data.certificate.deviceName ?? '登録済み'}（${CERT_STATUS_LABELS[client.data.certificate.status ?? 'active']}）`
                    : '提示されていますが未登録です'}
                </>
              ) : (
                <span className="bs-sec-kv__note">{client.data.clientCertRequired ? '提示されていません' : '使用していません'}</span>
              )}
            </dd>
          </dl>
        ) : (
          <Loading block />
        )}
      </Panel>

      {mode === 'disable' && (
        <PasswordDialog
          title="2段階認証を無効にしますか？"
          description="無効にすると、ログイン時に確認コードの入力が不要になります。認証アプリの登録とリカバリーコードは削除されます。確認のためパスワードを入力してください。"
          confirmLabel="無効にする"
          tone="danger"
          onClose={() => setMode('idle')}
          onSubmit={async (pw) => {
            await mfaApi.disable(pw);
            setMode('idle');
            toast.success('2段階認証を無効にしました');
            await refresh();
          }}
        />
      )}
      {mode === 'regenerate' && (
        <PasswordDialog
          title="リカバリーコードを再発行しますか？"
          description="新しいリカバリーコードを 10 個発行します。これまでのリカバリーコードは使えなくなります。確認のためパスワードを入力してください。"
          confirmLabel="再発行する"
          onClose={() => setMode('idle')}
          onSubmit={async (pw) => {
            const r = await mfaApi.regenerate(pw);
            setMode('idle');
            setCodes(r.recoveryCodes);
          }}
        />
      )}
      <p className="bs-page-footnote">
        <KeyRound size={13} aria-hidden /> スマートフォンを紛失・機種変更した場合は、振興基金の担当者に 2 段階認証のリセットを依頼してください。
      </p>
    </>
  );
}
